import { ComponentProps } from "react";
import { LineConfig } from "@ant-design/charts";
import * as _ from "lodash";

import { ChartSeriesType, DataDescriptorEntity, Enterprise as App } from "@ctra/api";
import { Enterprise, i18nTranslate } from "@ctra/i18n";
import { KPIThresholdDownV2, KPIThresholdUpV2 } from "@ctra/components";
import { Epoch, memoize, Units, UnitSystem } from "@ctra/utils";
import { ChartContext } from "@ctra/charts";

import { OnboardingInsightValidation, VulcanV2InsightBody, VulcanV2InsightHeader } from "@insights";
import { loadUnitSystem } from "@base";

import { GenericInsightBase } from "./Base";

interface VulcanV2InsightMetadata {
  stage: number;
  variantId: DataDescriptorEntity["id"];
  variantName: DataDescriptorEntity["dataProperties"]["typeName"];
  subjectType: ChartSeriesType; // or ChartFilterType
  points: Array<{ x: string; y: number }>;
  subject: string;
  changeRel: number;
  modelVersion: string;
  lengthDays: number;
}

const {
  kpi,
  insights: {
    entity: { titleParam }
  }
} = Enterprise;

/**
 * Automated KPI insight
 */
class VulcanV2Insight extends GenericInsightBase {
  @memoize
  getMetadata(): VulcanV2InsightMetadata {
    return super.getMetadata() as VulcanV2InsightMetadata;
  }

  getDescription(): string {
    return "";
  }

  /**
   * Convert the anomaly value to the current unit system
   * @param {number} value
   * @returns {number}
   */
  @memoize
  convertAnomalyValue(value: number): number {
    const variantID = this.getMetadata().variantId;
    const variant = App.entities.getDataDescriptor(App.store.getState(), { id: variantID });
    const units = variant?.valueProperties?.units;
    const globalUnitSystem = loadUnitSystem();

    return Units.convertToUnitSystem(value, units, { from: UnitSystem.metric, to: globalUnitSystem });
  }

  /**
   * Get parameters for translating the title and the description
   */
  @memoize
  getTranslationParams(): { kpi: string; groupName: string } {
    const { variantName: typeName, subjectType, subject } = this.getMetadata();

    let groupName = subject;

    switch (subjectType) {
      case "farm": {
        groupName = _.defaultTo(App.entities.getFarm(App.store.getState(), { farmID: +subject })?.name, "");
        break;
      }
      case "dimGroup": {
        groupName = _.defaultTo(App.entities.getDIMGroup(App.store.getState(), { id: subject }), {
          name: ""
        }).name;
        break;
      }
      case "penSubType": {
        groupName = i18nTranslate(titleParam(subjectType, subject));
        break;
      }
    }

    const kpiName = i18nTranslate(kpi.displayName(typeName), {
      variant: "short",
      case: null,
      makeDefaultValue: true
    });

    return {
      kpi: kpiName,
      groupName
    };
  }

  /**
   * Replace the icons based on the metadata and add its own validation
   */
  @memoize
  getComponents(): ReturnType<typeof GenericInsightBase.prototype.getComponents> {
    const { changeRel } = this.getMetadata();
    const components = super.getComponents();

    return {
      ...components,
      Header: VulcanV2InsightHeader,
      Body: VulcanV2InsightBody,
      Icon: changeRel > 0 ? KPIThresholdUpV2 : KPIThresholdDownV2,
      ListIcon: changeRel > 0 ? KPIThresholdUpV2 : KPIThresholdDownV2,
      Validation: OnboardingInsightValidation
    };
  }

  /**
   * Get the chart configuration to highlight the area of anomalies
   * @returns {React.ComponentProps<typeof ChartContext.Provider>["config"]}
   */
  @memoize
  getChartConfig(): ComponentProps<typeof ChartContext.Provider>["config"] {
    const { startEpoch } = this.entity;
    const { lengthDays } = this.getMetadata();

    /**
     * Pick the start date from the insight
     * @type {string}
     */
    const startDate = Epoch.asMoment(startEpoch).subtract(1, "day").format("YYYY-MM-DD");

    /**
     * Make an end date using the length of the anomaly
     * @type {string}
     */
    const endDate = Epoch.asMoment(startEpoch).add(lengthDays, "days").format("YYYY-MM-DD");

    /**
     * Make a region annotation
     * @type {{type: string, start: [string, string], end: [string, string], style: {fill: string, fillOpacity: number}}}
     */
    const annotation: NonNullable<LineConfig["annotations"]>[number] = {
      type: "region",
      start: [startDate, "min"],
      end: [endDate, "max"],
      style: {
        fill: "#FFCBD1",
        fillOpacity: 1
      }
    };

    return {
      annotations: [annotation]
    };
  }
}

export { VulcanV2Insight };
